import React, { useState, useEffect } from 'react';
import { CheckCircle, ArrowLeft, AlertCircle, ShieldCheck } from 'lucide-react';
import apiService from '../../apiService';

const CURRENCY = process.env.REACT_APP_CURRENCY_SYMBOL || '₹';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function submitPayuForm(formParams) {
  const { action, ...fields } = formParams || {};

  if (!action) {
    throw new Error('Invalid PayU payload: missing action URL');
  }

  const form = document.createElement('form');
  form.method = 'POST';
  form.action = action;
  form.style.display = 'none';

  Object.entries(fields).forEach(([name, value]) => {
    const input = document.createElement('input');
    input.type = 'hidden';
    input.name = name;
    input.value = value ?? '';
    form.appendChild(input);
  });

  document.body.appendChild(form);
  form.submit();
}

// ---------------------------------------------------------------------------
// PaymentModal
// ---------------------------------------------------------------------------

const PaymentModal = ({
  isOpen,
  onClose,
  paymentMethod,
  paymentGateway, // 'razorpay' or 'payu'
  totalAmount,
  orderId,
  userPhone,
  onPaymentComplete,
  payuEnabled = true,
  razorpayEnabled = false,
  codEnabled  = true,
  upiQrCode = '/static/upi-qr.jpeg',
  upiId = '',
  razorpayKey = '',
}) => {
  const [step, setStep]               = useState('idle'); // idle | loading | success | error
  const [errorMsg, setErrorMsg]       = useState('');
  const [processing, setProcessing]   = useState(false);


  // Handler for COD/UPI Scan on Delivery
  const handleCOD = async () => {
    try {
      setProcessing(true);
      // Simulate API call or order confirmation
      // You can replace this with your actual API logic
      await new Promise((resolve) => setTimeout(resolve, 1000));
      if (onPaymentComplete) onPaymentComplete();
    } catch (err) {
      setErrorMsg('Failed to confirm order. Please try again.');
    } finally {
      setProcessing(false);
    }
  };

  // Handler for PayU payment
  const handlePayU = async () => {
    if (!orderId) {
      setErrorMsg('Order ID is missing. Please try again.');
      return;
    }

    try {
      setProcessing(true);
      setErrorMsg('');

      const response = await apiService.initiatePayuPayment(orderId, userPhone || null);

      const formParams =
        response?.data?.form_params ||
        response?.form_params ||
        null;

      if (!formParams || !formParams.action) {
        const msg =
          response?.data?.message ||
          response?.message ||
          'Failed to initiate payment. Please try again.';
        setErrorMsg(msg);
        return;
      }

      submitPayuForm(formParams);
      // Page will redirect to PayU — no further state update needed
    } catch (err) {
      setErrorMsg('Failed to initiate PayU payment. Please try again.');
    } finally {
      setProcessing(false);
    }
  };

  // Handler for Razorpay payment
  const handleRazorpay = async () => {
    if (!orderId) {
      setErrorMsg('Order ID is missing. Please try again.');
      return;
    }

    try {
      setProcessing(true);
      setErrorMsg('');

      const response = await apiService.initiateRazorpayPayment(orderId, paymentMethod);
      
      if (!response || !response.success) {
        const msg = response?.data?.message || response?.message || 'Failed to initiate Razorpay payment.';
        setErrorMsg(msg);
        setProcessing(false);
        return;
      }

      const gatewayOrderId = response?.gateway_order_id || response?.data?.gateway_order_id;
      const paymentId = response?.payment_id || response?.data?.payment_id;
      const amount = response?.amount || response?.data?.amount;
      const prefill = response?.prefill || response?.data?.prefill;

      if (!gatewayOrderId || !razorpayKey) {
        setErrorMsg('Failed to initialize Razorpay. Please try again.');
        setProcessing(false);
        return;
      }

      // Initialize Razorpay
      const options = {
        key: razorpayKey,
        amount: amount,
        currency: 'INR',
        order_id: gatewayOrderId,
        handler: async (razorpayResponse) => {
          try {
            const verifyResponse = await apiService.verifyRazorpayPayment({
              payment_id: paymentId,
              razorpay_order_id: gatewayOrderId,
              razorpay_payment_id: razorpayResponse.razorpay_payment_id,
              razorpay_signature: razorpayResponse.razorpay_signature,
            });

            if (verifyResponse?.success) {
              if (onPaymentComplete) {
                onPaymentComplete({
                  orderId: orderId,
                  txnid: razorpayResponse.razorpay_payment_id,
                  paymentMethod: paymentMethod
                });
              }
            } else {
              setErrorMsg('Payment verification failed. Please contact support.');
              setProcessing(false);
            }
          } catch (err) {
            console.error('Razorpay verification error:', err);
            setErrorMsg('Payment verification error. Please try again.');
            setProcessing(false);
          }
        },
        prefill: prefill || {
          name: '',
          email: '',
          contact: userPhone || '',
        },
        theme: {
          color: '#2563eb'
        }
      };

      if (window.Razorpay) {
        const rzp = new window.Razorpay(options);
        rzp.on('payment.failed', function(response) {
          setErrorMsg(`Payment failed: ${response.error.description}`);
          setProcessing(false);
        });
        rzp.open();
      } else {
        setErrorMsg('Razorpay SDK not loaded. Please try again.');
        setProcessing(false);
      }
    } catch (err) {
      console.error('Razorpay error:', err);
      setErrorMsg('Failed to initiate Razorpay payment. Please try again.');
      setProcessing(false);
    }
  };

  if (!isOpen) return null;

  // Payment method labels and icons
  const methodLabels = {
    upi:        { label: 'UPI Payment',   icon: '📱', color: ''    },
    card:       { label: 'Card Payment',  icon: '💳', color: ''       },
    wallet:     { label: 'Wallet Payment',icon: '👛', color: ''       },
    netbanking: { label: 'Net Banking',   icon: '🏦', color: ''  },
  };

  // ── COD or UPI Scan on Delivery Screen ──
  if (paymentMethod === 'cod' || paymentMethod === 'upi_cod') {
    const isUpiCod = paymentMethod === 'upi_cod';

    const assurances = isUpiCod
      ? ['No online payment required in advance',
         'Pay securely via UPI at your doorstep',
         'Safe and contactless delivery']
      : ['No online payment required',
         'Pay only after receiving your order',
         'Safe and secure delivery'];

    const card = (children) => (
      <div className="bg-white p-4" style={{ border: '1px solid var(--fx-line)', borderRadius: 4 }}>
        {children}
      </div>
    );

    // Same shell as CheckoutModal: full-screen flex column, fixed header and
    // action bar, one scrolling body. This screen is the last step of that
    // flow, so it should not arrive as a differently-shaped popup card.
    return (
      <div className="fashion-scope fixed inset-0 z-[9999] bg-[#fafafa] flex flex-col overflow-hidden">
        {/* Header + steps */}
        <div className="bg-white flex-shrink-0" style={{ borderBottom: '1px solid var(--fx-line)' }}>
          <div className="flex items-center gap-3 px-4 py-3 w-full max-w-[1200px] mx-auto">
            <button onClick={onClose} className="p-1 -ml-1" aria-label="Go back">
              <ArrowLeft size={22} style={{ color: 'var(--fx-ink)' }} />
            </button>
            <span className="fx-label flex-1">
              {isUpiCod ? 'Cash on Delivery (UPI)' : 'Cash on Delivery'}
            </span>
            <span className="flex items-center gap-1.5" style={{ fontSize: 12, color: 'var(--fx-ink-2)' }}>
              <ShieldCheck size={14} style={{ color: 'var(--fx-green)' }} />
              100% Secure
            </span>
          </div>

          <div
            className="flex items-center justify-center gap-2 py-3 w-full max-w-[1200px] mx-auto"
            style={{ borderTop: '1px solid var(--fx-line)' }}
          >
            {['Bag', 'Address', 'Payment'].map((label, index) => (
              <React.Fragment key={label}>
                <span
                  style={{
                    fontSize: 13, fontWeight: 700, letterSpacing: '0.04em',
                    textTransform: 'uppercase',
                    // Payment is where we are; the two before it are done.
                    color: label === 'Payment' ? 'var(--fx-pink)' : 'var(--fx-ink)',
                  }}
                >
                  {label}
                </span>
                {index < 2 && <span style={{ width: 28, height: 1, background: 'var(--fx-line)' }} />}
              </React.Fragment>
            ))}
          </div>
        </div>

        {/* Body — the only scrolling region, matching checkout */}
        <div className="flex-1 min-h-0 overflow-y-auto fx-scroll">
          <div className="w-full max-w-[1200px] mx-auto px-3 sm:px-4 py-4 lg:flex lg:gap-6 lg:items-start">
            <div className="lg:flex-1 lg:min-w-0 space-y-3">
              {card(
                <>
                  <h3 className="fx-label mb-2">
                    {isUpiCod ? 'Pay via UPI QR at delivery' : 'Pay when you receive'}
                  </h3>
                  <p style={{ fontSize: 14, color: 'var(--fx-ink-2)', lineHeight: 1.6 }}>
                    {isUpiCod
                      ? 'Scan the UPI QR code at delivery to pay digitally. No cash required.'
                      : 'Keep exact change ready for a hassle-free delivery.'}
                  </p>
                </>
              )}

              {isUpiCod && card(
                <>
                  <h3 className="fx-label mb-3">Your UPI QR</h3>
                  <div className="flex flex-col items-center">
                    <img
                      src={upiQrCode}
                      alt="UPI QR Code"
                      style={{ width: 200, height: 200, display: 'block', borderRadius: 4 }}
                    />
                    {upiId && (
                      <p className="mt-2" style={{ fontSize: 14, fontWeight: 700 }}>{upiId}</p>
                    )}
                    <p className="mt-1" style={{ fontSize: 12, color: 'var(--fx-ink-2)' }}>
                      Show this QR to the delivery agent to pay
                    </p>
                  </div>
                </>
              )}

              {card(
                <>
                  <h3 className="fx-label mb-3">What this means</h3>
                  <div className="space-y-2.5">
                    {assurances.map((txt) => (
                      <div key={txt} className="flex items-center gap-2.5">
                        <CheckCircle size={16} style={{ color: 'var(--fx-green)', flexShrink: 0 }} />
                        <span style={{ fontSize: 14, color: 'var(--fx-ink-2)' }}>{txt}</span>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </div>

            {/* Amount panel — the price-details rail from checkout */}
            <div className="lg:w-[340px] lg:flex-shrink-0 lg:sticky lg:top-4 mt-3 lg:mt-0">
              {card(
                <>
                  <h3 className="fx-label mb-2">Amount to be collected</h3>
                  <div className="flex items-center justify-between py-1.5">
                    <span style={{ fontSize: 14, color: 'var(--fx-ink-2)' }}>Order total</span>
                    <span style={{ fontSize: 14 }}>{CURRENCY}{totalAmount}</span>
                  </div>
                  <div className="mt-2 pt-3" style={{ borderTop: '1px solid var(--fx-line)' }}>
                    <div className="flex items-center justify-between py-1.5">
                      <span style={{ fontSize: 14, fontWeight: 700 }}>Pay on delivery</span>
                      <span style={{ fontSize: 14, fontWeight: 700 }}>{CURRENCY}{totalAmount}</span>
                    </div>
                  </div>
                  <p className="mt-3" style={{ fontSize: 12, color: 'var(--fx-ink-2)' }}>
                    {isUpiCod
                      ? 'Nothing is charged now. You pay by UPI when the order arrives.'
                      : 'Nothing is charged now. You pay in cash when the order arrives.'}
                  </p>

                  <button
                    onClick={handleCOD}
                    disabled={processing}
                    className="fx-btn fx-btn-primary w-full mt-4 hidden lg:flex"
                  >
                    {processing ? 'Confirming…' : isUpiCod ? 'Confirm UPI on Delivery' : 'Confirm COD Order'}
                  </button>
                </>
              )}
            </div>
          </div>
        </div>

        {/* Action bar — a pane, not a fixed overlay, so it cannot cover content */}
        <div
          className="flex-shrink-0 bg-white flex items-center gap-3 px-3 py-2.5 lg:hidden"
          style={{ borderTop: '1px solid var(--fx-line)', boxShadow: '0 -2px 10px rgba(0,0,0,0.06)' }}
        >
          <div className="flex-shrink-0">
            <p style={{ fontSize: 17, fontWeight: 700, lineHeight: 1.1 }}>{CURRENCY}{totalAmount}</p>
            <p style={{ fontSize: 11, color: 'var(--fx-ink-2)' }}>on delivery</p>
          </div>
          <button
            onClick={handleCOD}
            disabled={processing}
            className="fx-btn fx-btn-primary flex-1"
          >
            {processing
              ? <span className="flex items-center justify-center gap-2">
                  <span className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
                  Confirming…
                </span>
              : isUpiCod ? 'Confirm UPI on Delivery' : 'Confirm COD Order'}
          </button>
        </div>
      </div>
    );
  }

  // ── Online Payment (UPI / Card / Wallet / Netbanking) via PayU or Razorpay ──────────
  const current = methodLabels[paymentMethod] || { label: 'Online Payment', icon: '🔒', color: '' };
  const isRazorpay = paymentGateway === 'razorpay';

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-end sm:items-center justify-center z-[9999] p-0 sm:p-4">
      <div className="bg-white w-full sm:max-w-md sm:rounded rounded-t p-6 max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg">
            <ArrowLeft size={24} />
          </button>
          <h2 className="text-xl font-bold">{current.label}</h2>
          <div className="w-10" />
        </div>
        {/* Amount banner */}
        <div className={`bg-gradient-to-br ${current.color} rounded p-6 mb-6 text-white text-center`}>
          <div className="text-4xl mb-2">{current.icon}</div>
          <div className="text-sm opacity-90 mb-1">Amount to Pay</div>
          <div className="text-4xl font-bold">₹{totalAmount}</div>
        </div>
        {/* Gateway info */}
        <div className="bg-[#fff8fa] rounded p-4 mb-6">
          <div className="flex items-start gap-3">
            <img
              src={isRazorpay 
                ? "https://rzp.io/i/w2CEwM3" 
                : "https://upload.wikimedia.org/wikipedia/commons/thumb/7/71/PayU.svg/200px-PayU.svg.png"}
              alt={isRazorpay ? "Razorpay" : "PayU"}
              className="w-16 h-auto mt-1"
              onError={(e) => { e.target.style.display = 'none'; }}
            />
            <div>
              <p className="font-semibold text-[#282c3f] text-sm">Powered by {isRazorpay ? 'Razorpay' : 'PayU'}</p>
              <p className="text-xs text-[#94969f] mt-1">
                {isRazorpay 
                  ? 'Your payment will be processed securely through Razorpay.'
                  : 'You will be redirected to PayU\'s secure checkout. After payment, you will return here automatically.'}
              </p>
            </div>
          </div>
        </div>
        {/* Accepted methods */}
        <div className="flex gap-2 mb-6 flex-wrap">
          {['Visa', 'Mastercard', 'UPI', 'Paytm', 'PhonePe', 'Net Banking'].map((m) => (
            <span key={m} className="text-xs bg-gray-100 text-[#535766] px-2 py-1 rounded-full">{m}</span>
          ))}
        </div>
        {/* Security */}
        <div className="flex items-center gap-2 text-xs text-[#94969f] mb-6">
          <span>🔒</span>
          <span>256-bit SSL encryption · PCI-DSS compliant · {isRazorpay ? 'Razorpay certified' : '3D Secure'}</span>
        </div>
        {/* Error message */}
        {errorMsg && (
          <div className="flex items-start gap-2 bg-red-50 border border-red-200 rounded p-3 mb-4 text-sm text-red-700">
            <AlertCircle size={16} className="flex-shrink-0 mt-0.5" />
            {errorMsg}
          </div>
        )}
        {/* Pay button */}
        <button
          onClick={isRazorpay ? handleRazorpay : handlePayU}
          disabled={processing}
          className={`w-full py-4 rounded font-bold text-lg transition-all ${
            processing
              ? 'bg-gray-300 text-[#94969f] cursor-not-allowed'
              : 'bg-[#ff3f6c] text-white hover:bg-[#e03a60]'
          }`}
        >
          {processing
            ? <span className="flex items-center justify-center gap-2">
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white" />
                {isRazorpay ? 'Connecting to Razorpay…' : 'Connecting to PayU…'}
              </span>
            : `Pay ₹${totalAmount} via ${isRazorpay ? 'Razorpay' : 'PayU'}`
          }
        </button>
        <p className="text-center text-xs text-[#94969f] mt-3">
          {isRazorpay 
            ? 'A secure Razorpay page will open for payment.' 
            : 'A secure PayU page will open for payment and redirect you back after completion.'}
        </p>
      </div>
    </div>
  );
};
export default PaymentModal;
